import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../context/SocketContext';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import CandleChart from '../components/trade/CandleChart';
import { tradeService } from '../services/tradeService';
import { userService } from '../services/userService';
import UserLanguageSwitcher from '../components/common/UserLanguageSwitcher';
import './MajesticCommon.css';
import './TradePage.css';

const TradePage = () => {
    const { symbol: assetSymbol } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const socket = useSocket();

    const [activeTrades, setActiveTrades] = useState([]);
    const [showTradeModal, setShowTradeModal] = useState(false);
    const [tradeType, setTradeType] = useState('buy');
    const [amount, setAmount] = useState('');
    const [duration, setDuration] = useState(60);
    const [balance, setBalance] = useState(0);
    const [user, setUser] = useState({ currency: 'None' });
    const [leverage, setLeverage] = useState(1);
    const [profitRate, setProfitRate] = useState(30);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmedTradeData, setConfirmedTradeData] = useState(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const [priceData, setPriceData] = useState({ price: '0.00', high: '0.00', low: '0.00', open: '0.00', change24h: '0.00' });
    const [chartInterval, setChartInterval] = useState('5m');
    const [showResultModal, setShowResultModal] = useState(false);
    const [lastTradeResult, setLastTradeResult] = useState(null);


    const intervals = [
        { label: '1m', value: '1m' },
        { label: '5m', value: '5m' },
        { label: '15m', value: '15m' },
        { label: '30m', value: '30m' },
        { label: '1h', value: '1h' },
        { label: '1d', value: '1d' }
    ];

    const handlePriceUpdate = useCallback((data) => {
        setPriceData(data);
    }, []);

    // Listen for events via Socket
    useEffect(() => {
        if (!socket) return;

        const handleTradeResult = (data) => {
            const isWin = data.status === 'won';

            // Set result for modal
            setLastTradeResult({
                ...data,
                isWin
            });
            setShowResultModal(true);

            // Clear from active list
            setActiveTrades(prev => prev.filter(t => t.id !== data.tradeId));
        };

        const handleBalanceUpdate = (data) => {
            if (data.balance !== undefined) {
                setBalance(parseFloat(data.balance));
            }
        };

        socket.on('trade_result', handleTradeResult);
        socket.on('balance_update', handleBalanceUpdate);

        return () => {
            socket.off('trade_result', handleTradeResult);
            socket.off('balance_update', handleBalanceUpdate);
        };
    }, [socket, t]);

    // Stable countdown timer using expiration timestamp
    const [expiryTime, setExpiryTime] = useState(null);

    useEffect(() => {
        if (!showConfirmModal || !expiryTime) {
            setTimeLeft(0);
            return;
        }

        let timerId;
        const updateTimer = () => {
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((expiryTime - now) / 1000));
            setTimeLeft(remaining);
            if (remaining <= 0) {
                if (timerId) clearInterval(timerId);
                setShowConfirmModal(false);
                setExpiryTime(null);
                hasInitializedRef.current = false;
            }
        };

        // Initial update
        updateTimer();

        timerId = setInterval(updateTimer, 1000); // 1 second intervals for stability
        return () => clearInterval(timerId);
    }, [showConfirmModal, expiryTime]);

    const fetchTrades = useCallback(async () => {
        try {
            const data = await tradeService.getHistory();
            if (data.success && data.trades) {
                setActiveTrades(data.trades.filter(t => t.status === 'pending'));
            }
        } catch (err) { console.error(err); }
    }, []);

    const fetchProfile = useCallback(async () => {
        try {
            const data = await tradeService.getProfile ? await tradeService.getProfile() : await userService.getProfile();
            if (data.success) {
                setUser(data.user);
                setBalance(data.user.accountBalance);
            }
        } catch (err) { console.error(err); }
    }, []);

    // Initial fetch only
    useEffect(() => {
        fetchTrades();
        fetchProfile();
    }, [fetchTrades, fetchProfile]);

    // Track if we've already initialized from navigation state
    const hasInitializedRef = React.useRef(false);

    // Check for redirection state to show confirmation modal
    // Check for redirection state to show confirmation modal (supporting both state and sessionStorage)
    useEffect(() => {
        const checkConfirmation = () => {
            let tradeData = null;
            let shouldShow = false;

            // Priority 1: Check sessionStorage (New flow)
            const stored = sessionStorage.getItem('pendingTradeConfirmation');
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    // Only use if recent (less than 5 seconds ago) to avoid stale popups
                    if (Date.now() - parsed.tradeData.timestamp < 5000) {
                        tradeData = parsed.tradeData;
                        shouldShow = true;
                    }
                    sessionStorage.removeItem('pendingTradeConfirmation');
                } catch (e) { }
            }

            // Priority 2: Check location state (Legacy flow support)
            if (!shouldShow && location.state?.showConfirmation && location.state?.tradeData) {
                tradeData = location.state.tradeData;
                shouldShow = true;
                // Clear state
                navigate(location.pathname, { replace: true, state: {} });
            }

            if (shouldShow && tradeData && !hasInitializedRef.current) {
                hasInitializedRef.current = true; // Use ref to prevent double-firing

                // Set expiry time first, then show modal
                const expiry = Date.now() + (tradeData.duration * 1000);
                setConfirmedTradeData(tradeData);
                setExpiryTime(expiry);
                setShowConfirmModal(true);

                // Refresh trades list to include the new one
                fetchTrades();
            }
        };

        checkConfirmation();
    }, [location.state, fetchTrades, navigate, location.pathname]);

    const openTradeModal = (type) => {
        setTradeType(type);
        setShowTradeModal(true);
    };

    const handlePlaceTrade = async (e) => {
        e.preventDefault();
        if (!amount || parseFloat(amount) <= 0) {
            toast.error(t('msg_enter_valid_amount', 'Please enter a valid amount'));
            return;
        }

        try {
            const data = await tradeService.placeTrade({
                pair: assetSymbol,
                type: tradeType,
                amount: parseFloat(amount),
                entryPrice: parseFloat(priceData.price),
                duration: duration,
                leverage: leverage,
                profitRate: profitRate
            });

            if (data.success) {
                toast.success(t('trade_placed_success', 'Trade placed successfully!'));
                setShowTradeModal(false);
                setAmount('');

                setConfirmedTradeData({
                    direction: tradeType,
                    amount: parseFloat(amount),
                    purchasePrice: parseFloat(priceData.price),
                    leverage: leverage,
                    duration: duration
                });
                setExpiryTime(Date.now() + (duration * 1000));
                setShowConfirmModal(true);
            }
        } catch (error) {
            toast.error(t(error.message || 'trade_failed'));
        }
    };

    const currencyMap = {
        'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
        'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
        'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
        'AED': 'د.إ', 'SAR': 'ر.س'
    };
    const currencySymbol = user?.username !== undefined && user?.currency !== undefined ? (currencyMap[user.currency] !== undefined ? currencyMap[user.currency] : '') : '';

    return (
        <div className="majestic-page-wrapper trade-dashboard-wrapper">
            {/* Majestic Header for Trade */}
            <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between">
                <div style={{ width: '40px' }}>
                    <i className="bi bi-chevron-left text-white fs-4 cursor-pointer" onClick={() => navigate(-1)}></i>
                </div>
                <div className="flex-grow-1 text-center d-flex flex-column align-items-center justify-content-center">
                    <div className="d-flex align-items-center gap-2">
                        <h1 className="majestic-header-title m-0" style={{ fontSize: '18px' }}>{assetSymbol}</h1>
                        <div className="live-indicator-dot" title={t('realtime_live', 'Real-time Live')}></div>
                    </div>
                    <div className="text-warning fw-bold" style={{ fontSize: '12px' }}>
                        {currencySymbol}{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                </div>
                <div style={{ width: '40px' }} className="d-flex justify-content-end">
                    <UserLanguageSwitcher />
                </div>
            </div>

            {/* Price Info Bar */}
            <div className="price-info-bar d-flex justify-content-between align-items-center">
                <div className="text-white display-5 fw-bold main-price-display">
                    {priceData.price}
                </div>
                <div className="price-stats-grid">
                    <div className="stat-item">
                        <span className="stat-label">{t('high', 'HIGH')}</span>
                        <span className="stat-value">{priceData.high}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">{t('low', 'LOW')}</span>
                        <span className="stat-value text-danger">{priceData.low}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">{t('open', 'OPEN')}</span>
                        <span className="stat-value">{priceData.open}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">{t('label_24h', '24H')}</span>
                        <span className={`stat-value ${parseFloat(priceData.change24h) >= 0 ? 'text-success' : 'text-danger'}`}>{parseFloat(priceData.change24h) >= 0 ? '+' : ''}{priceData.change24h}%</span>
                    </div>
                </div>
            </div>

            {/* Interval Selector */}
            <div className="interval-selector-container">
                <div className="segmented-pill">
                    {intervals.map((int) => (
                        <div
                            key={int.value}
                            className={`pill-item ${chartInterval === int.value ? 'active' : ''}`}
                            onClick={() => setChartInterval(int.value)}
                        >
                            {int.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart */}
            <div className="trade-chart-outer">
                <CandleChart symbol={assetSymbol} interval={chartInterval} onPriceUpdate={handlePriceUpdate} />
            </div>

            {/* Active Trades Section */}
            <div className="active-trades-container px-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="section-title opacity-50 small fw-bold">{t('active_trades', 'ACTIVE TRADES')}</span>
                    <span className="badge bg-warning text-dark">{activeTrades.length}</span>
                </div>
                <div className="active-trades-list d-flex flex-column gap-2 mb-4">
                    {activeTrades.map((trade) => (
                        <ActiveTradeCard key={trade.id} trade={trade} t={t} />
                    ))}
                    {activeTrades.length === 0 && (
                        <div className="text-center py-4 opacity-25">
                            <i className="bi bi-activity fs-2 d-block mb-2"></i>
                            <span className="small">{t('no_active_trades', 'No active trades')}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Actions - Now Redirects to Page */}
            <div className="trade-actions-footer">
                <button
                    className="btn-premium-action btn-up-gradient"
                    onClick={() => navigate(`/order/${assetSymbol}/buy`)}
                >
                    <span className="action-label">{t('buy_up', 'Buy Up')}</span>
                    <span className="action-sub">{t('call_option', 'Call Option')}</span>
                </button>
                <button
                    className="btn-premium-action btn-down-gradient"
                    onClick={() => navigate(`/order/${assetSymbol}/sell`)}
                >
                    <span className="action-label">{t('sell_down', 'Buy Down')}</span>
                    <span className="action-sub">{t('put_option', 'Put Option')}</span>
                </button>
            </div>

            {/* Trade Confirmation Modal - Match image */}
            <Modal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                centered
                className="confirm-trade-modal"
                contentClassName="glass-card border-0 shadow-lg"
            >
                <div className="confirm-modal-header">
                    <i className="bi bi-x-lg close-icon" onClick={() => {
                        setShowConfirmModal(false);
                        setExpiryTime(null);
                        hasInitializedRef.current = false;
                    }}></i>
                </div>
                <Modal.Body className="confirm-modal-body text-center p-4">
                    {/* Premium Countdown Timer */}
                    <div className="premium-timer-container">
                        {/* Outer decorative ring */}
                        <div className="timer-outer-ring"></div>

                        {/* Main SVG Timer */}
                        <svg className="timer-svg" viewBox="0 0 120 120">
                            <defs>
                                <linearGradient id="timerGradientBuy" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#0ecb81" />
                                    <stop offset="50%" stopColor="#00ffa3" />
                                    <stop offset="100%" stopColor="#0ecb81" />
                                </linearGradient>
                                <linearGradient id="timerGradientSell" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#f6465d" />
                                    <stop offset="50%" stopColor="#ff6b7a" />
                                    <stop offset="100%" stopColor="#f6465d" />
                                </linearGradient>
                                <filter id="glow">
                                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>
                            {/* Background track */}
                            <circle className="timer-track" cx="60" cy="60" r="50"></circle>
                            {/* Progress ring */}
                            <circle
                                className="timer-progress-ring"
                                cx="60" cy="60" r="50"
                                style={{
                                    strokeDashoffset: (confirmedTradeData && confirmedTradeData.duration) ? (314.16 - (314.16 * timeLeft) / confirmedTradeData.duration) : 314.16,
                                    stroke: confirmedTradeData?.direction === 'buy' ? 'url(#timerGradientBuy)' : 'url(#timerGradientSell)'
                                }}
                                filter="url(#glow)"
                            ></circle>
                        </svg>

                        {/* Center content */}
                        <div className="timer-center-content">
                            <div className="timer-seconds">{Math.max(0, timeLeft)}<span className="timer-unit">s</span></div>
                            <div className="timer-divider"></div>
                            <div className="timer-price-label">{t('current_price', 'Current Price')}</div>
                            <div className="timer-price-value" style={{ color: confirmedTradeData?.direction === 'buy' ? '#0ecb81' : '#f6465d' }}>{priceData.price}</div>
                        </div>
                    </div>


                    {/* Order Details Grid */}
                    <div className="confirm-details-row mt-4">
                        <div className="confirm-detail-item">
                            <div className="label">{t('direction', 'Direction')}</div>
                            <div className="value">{confirmedTradeData?.direction === 'buy' ? t('buy_up', 'Buy Up') : t('buy_down', 'Buy Down')}</div>
                        </div>
                        <div className="confirm-detail-item">
                            <div className="label">{t('amount', 'Amount')}</div>
                            <div className="value">{confirmedTradeData?.amount?.toFixed(2) || '0.00'}</div>
                        </div>
                        <div className="confirm-detail-item">
                            <div className="label">{t('purchase_price', 'Purchase Price')}</div>
                            <div className="value">{confirmedTradeData?.purchasePrice?.toFixed(2) || '0.00'}</div>
                        </div>
                        <div className="confirm-detail-item">
                            <div className="label">{t('leverage_ratio', 'Leverage Ratio')}</div>
                            <div className="value">x{confirmedTradeData?.leverage}</div>
                        </div>
                    </div>

                    <Button
                        className="continue-ordering-btn mt-4"
                        onClick={() => {
                            setShowConfirmModal(false);
                            setExpiryTime(null);
                        }}
                    >
                        {t('continue_ordering', 'Continue Ordering')}
                    </Button>
                </Modal.Body>
            </Modal>

            {/* Premium Trade Result Modal */}
            <Modal
                show={showResultModal}
                onHide={() => setShowResultModal(false)}
                centered
                className="trade-result-modal"
                contentClassName="glass-card border-0 shadow-lg"
            >
                <Modal.Body className="text-center p-5">
                    <div className={`result-icon-container mb-4 ${lastTradeResult?.isWin ? 'won' : 'lost'}`}>
                        <div className="result-glow"></div>
                        <i className={`bi ${lastTradeResult?.isWin ? 'bi-trophy-fill' : 'bi-x-circle-fill'}`}></i>
                    </div>

                    <h2 className={`result-title mb-2 ${lastTradeResult?.isWin ? 'text-success' : 'text-danger'}`}>
                        {lastTradeResult?.isWin ? t('trade_won', 'TRADE WON!') : t('trade_lost', 'TRADE LOST')}
                    </h2>

                    <p className="text-secondary small mb-4">
                        {lastTradeResult?.isWin
                            ? t('msg_win_congrats', 'Congratulations! Your prediction was correct.')
                            : t('msg_loss_unfortunate', 'Unfortunate. Better luck next time.')
                        }
                    </p>

                    <div className="result-details-box mb-4">
                        <div className="d-flex justify-content-between mb-2">
                            <span className="label opacity-50">{t('asset', 'Asset')}</span>
                            <span className="value text-white">{lastTradeResult?.pair}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span className="label opacity-50">{t('pnl', 'PnL')}</span>
                            <span className={`value fw-bold ${lastTradeResult?.isWin ? 'text-success' : 'text-danger'}`}>
                                {lastTradeResult?.isWin ? '+' : ''}{lastTradeResult?.pnl?.toFixed(2)}
                            </span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="label opacity-50">{t('exit_price', 'Exit Price')}</span>
                            <span className="value text-white">{lastTradeResult?.exitPrice}</span>
                        </div>
                    </div>

                    <Button
                        className={`w-100 py-3 rounded-pill fw-bold border-0 ${lastTradeResult?.isWin ? 'btn-success bg-gradient' : 'btn-danger bg-gradient'}`}
                        onClick={() => setShowResultModal(false)}
                    >
                        {t('acknowledge', 'Got it')}
                    </Button>
                </Modal.Body>
            </Modal>
        </div>
    );
};


// Sub-component for Active Trade Card matching Image 2
const ActiveTradeCard = ({ trade, t }) => {
    const [progress, setProgress] = useState(100);
    const duration = trade.duration || 60;

    useEffect(() => {
        const startTime = new Date(trade.createdAt).getTime();
        const endTime = startTime + (duration * 1000);

        const updateProgress = () => {
            const now = Date.now();
            const total = duration * 1000;
            const remaining = endTime - now;
            const newProgress = Math.max(0, (remaining / total) * 100);
            setProgress(newProgress);
        };

        const interval = setInterval(updateProgress, 100);
        return () => clearInterval(interval);
    }, [trade.createdAt, duration]);

    return (
        <div className="active-trade-card-horizontal">
            <div className="card-top-row d-flex justify-content-between">
                <div className="asset-info">
                    <span className="symbol">{trade.pair}</span>
                    <span className={`direction ${trade.type === 'buy' ? 'text-success' : 'text-danger'} ms-2`}>
                        {trade.type === 'buy' ? t('buy_up', 'Buy Up') : t('buy_down', 'Buy Down')}
                    </span>
                </div>
                <div className="status-label opacity-50 small">{t('pending', 'Pending')}</div>
            </div>

            <div className="card-details-grid mt-2">
                <div className="detail-col">
                    <div className="label">{t('investment_amount', 'Investment Amount')}</div>
                    <div className="value">{trade.amount.toFixed(2)}</div>
                </div>
                <div className="detail-col">
                    <div className="label">{t('opening_price', 'Opening Price')}</div>
                    <div className="value">{trade.entryPrice.toFixed(2)}</div>
                </div>
                <div className="detail-col text-end">
                    <div className="label">{t('opening_time', 'Opening Time')}</div>
                    <div className="value">{new Date(trade.createdAt).toLocaleString('en-GB', { hour12: false })}</div>
                </div>
            </div>

            <div className="leverage-info mt-2 small">
                <span className="text-success">{t('leverage_ratio', 'Leverage Ratio')} x{trade.leverage || 1}</span>
            </div>

            <div className="progress-bar-container mt-2">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
        </div>
    );
};

export default TradePage;
